import { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./ui/select";
import { Mail, Phone, Briefcase, Download, Save, Calendar, FileText } from "lucide-react";
import { api, API, tokenStore, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { APPLICATION_STATUSES, getStatusInfo, formatDateTime } from "../lib/adminConstants";
import { KILOBYTE } from "../lib/constants";

export default function ApplicationDetailModal({ application, open, onOpenChange, onUpdated }) {
    const [status, setStatus] = useState("new");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (application) {
            setStatus(application.status || "new");
            setNotes(application.notes || "");
        }
    }, [application]);

    if (!application) return null;

    const statusInfo = getStatusInfo(APPLICATION_STATUSES, status);

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/admin/applications/${application.id}`, { status, notes });
            toast.success("Aktualisiert");
            onUpdated?.({ ...application, status, notes });
            onOpenChange(false);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const downloadCV = () => {
        const t = tokenStore.get();
        const url = `${API}/admin/applications/${application.id}/cv${t ? `?auth=${encodeURIComponent(t)}` : ""}`;
        window.open(url, "_blank");
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="application-detail-modal"
                className="max-w-2xl max-h-[90vh] overflow-y-auto p-0 gap-0"
            >
                {/* Header */}
                <div className="bg-aimploy-navy text-white p-6 lg:p-8 rounded-t-lg">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl lg:text-3xl font-bold text-white">
                            {application.name}
                        </DialogTitle>
                        <DialogDescription className="text-white/70 mt-1">
                            Bewerbung für <strong className="text-white">{application.position}</strong>
                        </DialogDescription>
                    </DialogHeader>
                    <div className={`inline-flex items-center gap-2 mt-4 px-3 py-1.5 rounded-full text-xs font-semibold ${statusInfo.color.replace("text-", "text-white bg-").split(" ")[0].replace("bg-", "bg-")} bg-white/10 border border-white/20`}>
                        <span className="h-2 w-2 rounded-full bg-aimploy-violet" />
                        {statusInfo.label}
                    </div>
                </div>

                <div className="p-6 lg:p-8 space-y-6">
                    {/* Info grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field icon={Mail} label="E-Mail" value={<a href={`mailto:${application.email}`} className="hover:text-aimploy-violet">{application.email}</a>} />
                        {application.phone && <Field icon={Phone} label="Telefon" value={<a href={`tel:${application.phone}`} className="hover:text-aimploy-violet">{application.phone}</a>} />}
                        <Field icon={Briefcase} label="Position" value={application.position} />
                        <Field icon={Calendar} label="Eingegangen" value={formatDateTime(application.created_at)} />
                    </div>

                    {/* Message */}
                    {application.message && (
                        <div>
                            <div className="text-xs uppercase tracking-[0.15em] text-aimploy-muted font-semibold mb-2">Nachricht</div>
                            <div className="bg-aimploy-bg rounded-xl p-4 text-sm leading-relaxed whitespace-pre-line">
                                {application.message}
                            </div>
                        </div>
                    )}

                    {/* CV */}
                    <div>
                        <div className="text-xs uppercase tracking-[0.15em] text-aimploy-muted font-semibold mb-2">Lebenslauf</div>
                        <button
                            onClick={downloadCV}
                            data-testid="modal-cv-download"
                            className="w-full flex items-center justify-between gap-3 bg-aimploy-bg hover:bg-aimploy-violet/10 border border-black/5 hover:border-aimploy-violet/30 rounded-xl p-4 transition-all group"
                        >
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="h-10 w-10 rounded-lg bg-aimploy-navy text-white flex items-center justify-center flex-shrink-0">
                                    <FileText className="w-5 h-5" />
                                </div>
                                <div className="text-left min-w-0">
                                    <div className="font-semibold truncate">{application.cv_filename || "Lebenslauf"}</div>
                                    <div className="text-xs text-aimploy-muted">
                                        {((application.cv_size || 0) / KILOBYTE).toFixed(0)} KB · {application.cv_content_type}
                                    </div>
                                </div>
                            </div>
                            <Download className="w-5 h-5 text-aimploy-violet group-hover:translate-y-0.5 transition-transform flex-shrink-0" />
                        </button>
                    </div>

                    {/* Status */}
                    <div>
                        <div className="text-xs uppercase tracking-[0.15em] text-aimploy-muted font-semibold mb-2">Status</div>
                        <Select value={status} onValueChange={setStatus}>
                            <SelectTrigger data-testid="modal-status-select" className="w-full">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {APPLICATION_STATUSES.map((s) => (
                                    <SelectItem key={s.value} value={s.value}>
                                        {s.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Notes */}
                    <div>
                        <div className="text-xs uppercase tracking-[0.15em] text-aimploy-muted font-semibold mb-2">Interne Notizen</div>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={4}
                            data-testid="modal-notes"
                            placeholder="Interview-Feedback, Bewertung, nächste Schritte…"
                            className="w-full border border-aimploy-navy/15 rounded-xl px-4 py-3 text-sm resize-none"
                        />
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3 pt-2">
                        <button
                            onClick={() => onOpenChange(false)}
                            className="flex-1 px-5 py-3 rounded-xl border border-aimploy-navy/15 text-sm font-semibold hover:bg-aimploy-bg"
                        >
                            Schließen
                        </button>
                        <button
                            onClick={save}
                            disabled={saving}
                            data-testid="modal-save"
                            className="flex-1 inline-flex items-center justify-center gap-2 bg-aimploy-navy text-white px-5 py-3 rounded-xl text-sm font-semibold hover:bg-aimploy-navy-hover disabled:opacity-60"
                        >
                            <Save className="w-4 h-4" />
                            {saving ? "Speichern…" : "Speichern"}
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function Field({ icon: Icon, label, value }) {
    return (
        <div className="flex items-start gap-3">
            <div className="h-8 w-8 rounded-lg bg-aimploy-violet/10 text-aimploy-violet flex items-center justify-center flex-shrink-0">
                <Icon className="w-4 h-4" />
            </div>
            <div className="min-w-0">
                <div className="text-xs uppercase tracking-[0.15em] text-aimploy-muted font-semibold mb-0.5">{label}</div>
                <div className="text-sm font-medium truncate">{value}</div>
            </div>
        </div>
    );
}
